import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../AppIcon';
import { useLanguage } from '../../contexts/LanguageContext';

const UserProfileDropdown = ({ user, onLogout, onLanguageChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { t, currentLanguage, changeLanguage } = useLanguage();

  const userInfo = {
    name: user?.name || 'Utilisateur',
    role: user?.role || 'Agent',
    agency: user?.agency || 'Agence',
    avatar: user?.avatar || null
  };

  const roleLabels = {
    fr: {
      super_admin: 'Super Admin',
      agency_admin: 'Administrateur',
      travel_agent: 'Agent de voyage',
      accountant: 'Comptable'
    },
    ar: {
      super_admin: 'المشرف الأعلى',
      agency_admin: 'المدير',
      travel_agent: 'وكيل السفر',
      accountant: 'المحاسب'
    }
  };

  const menuItems = [
    {
      icon: 'UserCog',
      label: t('common.profile'),
      path: '/user-profile-settings'
    },
    {
      icon: 'Settings',
      label: t('common.settings'),
      onClick: () => console.log('Settings clicked')
    },
    {
      icon: 'HelpCircle',
      label: t('common.help'),
      path: null
    }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event?.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleMenuAction = (action) => {
    setIsOpen(false);
    if (action === 'profile') {
      console.log('Navigate to profile');
    } else if (action === 'settings') {
      console.log('Navigate to settings');
    }
  };

  const handleLanguageToggle = () => {
    const newLanguage = currentLanguage === 'fr' ? 'ar' : 'fr';
    changeLanguage(newLanguage);
    if (onLanguageChange) {
      onLanguageChange(newLanguage);
    }
  };

  const handleLogoutClick = () => {
    setIsOpen(false);
    onLogout?.();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        className="flex items-center gap-3 px-4 py-2 rounded-md bg-card hover:bg-muted transition-smooth shadow-warm hover:shadow-warm-md"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          {userInfo?.avatar ? (
            <img 
              src={userInfo?.avatar} 
              alt={userInfo?.name}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            <Icon name="User" size={20} className="text-primary" />
          )}
        </div>
        <div className="hidden md:block text-left">
          <p className="text-sm font-medium text-foreground">{userInfo?.name}</p>
          <p className="caption text-muted-foreground">
            {t(`roles.${userInfo?.role}`) || userInfo?.role}
          </p>
        </div>
        <Icon 
          name={isOpen ? 'ChevronUp' : 'ChevronDown'} 
          size={16} 
          className="text-muted-foreground"
        />
      </button>
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 max-w-[calc(100vw-2rem)] bg-popover border border-border rounded-md shadow-warm-lg z-[100] overflow-hidden">
          <div className="p-4 border-b border-border bg-card">
            <p className="font-medium text-foreground">{userInfo?.name}</p>
            <p className="caption text-muted-foreground mt-1">
              {t(`roles.${userInfo?.role}`) || userInfo?.role}
            </p>
            <p className="caption text-muted-foreground">{userInfo?.agency}</p>
          </div>

          <div className="py-2">
            {menuItems?.map((item, index) => (
              item?.path ? (
                <Link
                  key={index}
                  to={item?.path}
                  onClick={() => setIsOpen(false)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted transition-smooth"
                >
                  <Icon name={item?.icon} size={18} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">
                    {item?.label}
                  </span>
                </Link>
              ) : (
                <button
                  key={index}
                  onClick={() => {
                    setIsOpen(false);
                    item?.onClick?.();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted transition-smooth"
                >
                  <Icon name={item?.icon} size={18} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">
                    {item?.label}
                  </span>
                </button>
              )
            ))}

            <button
              onClick={handleLanguageToggle}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted transition-smooth border-t border-border"
            >
              <Icon name="Languages" size={18} className="text-muted-foreground" />
              <span className="text-sm text-foreground">
                {currentLanguage === 'fr' ? 'العربية' : 'Français'}
              </span>
            </button>

            <button
              onClick={handleLogoutClick}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-destructive/10 transition-smooth border-t border-border text-destructive"
            >
              <Icon name="LogOut" size={18} />
              <span className="text-sm font-medium">
                {t('common.logout')}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileDropdown;